import React, { useEffect, useState } from "react";
import {
  GoogleMap,
  Marker,
  LoadScript,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { Clock, MapPin, Navigation2, TrendingUp, Truck } from "lucide-react";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { MAPS_CONFIG } from "../config";
import { API_URL } from '../config';
import { AlertTriangle } from "lucide-react";

const TripTracker2 = () => {
  const { tripId } = useParams();
  const [tripData, setTripData] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [loadingDriver, setLoadingDriver] = useState(true);
  const [directions, setDirections] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isOffRoute, setIsOffRoute] = useState(false);

  // Calculate trip statistics
  const calculateETA = () => {
    if (driverLocation && tripData) {
      const destinationLat = tripData[0].destination_lat;
      const destinationLng = tripData[0].destination_long;

      const distanceToDestination = haversineDistance(
        driverLocation.latitude,
        driverLocation.longitude,
        destinationLat,
        destinationLng
      );

      // Assuming average speed of 40 km/h
      const avgSpeed = 40;
      const etaMinutes = Math.round((distanceToDestination / avgSpeed) * 60);
      return etaMinutes;
    }
    return null;
  };

  const calculateCostPerKm = () => {
    if (tripData && tripData[0]) {
      const trip = tripData[0];
      if (trip.delivery_cost_proposed && trip.distance) {
        const costPerKm = parseFloat(trip.delivery_cost_proposed) / parseFloat(trip.distance);
        return costPerKm.toFixed(2);
      }
    }
    return null;
  };

  // Custom car marker SVG - Professional dual-layer design
  const carMarkerIcon = {
    path: "M23.5 7c.276 0 .5.224.5.5v.511c0 .793-.926.989-1.616.989l-1.086-2h2.202zm-1.441 3.506c.639 1.186.946 2.252.946 3.666 0 1.37-.397 2.533-1.005 3.981v1.847c0 .552-.448 1-1 1h-1.5c-.552 0-1-.448-1-1v-1h-13v1c0 .552-.448 1-1 1h-1.5c-.552 0-1-.448-1-1v-1.847c-.608-1.448-1.005-2.611-1.005-3.981 0-1.414.307-2.48.946-3.666.829-1.537 1.851-3.453 2.93-5.252.828-1.382 1.262-1.707 2.278-1.889 1.532-.275 2.918-.365 4.851-.365s3.319.09 4.851.365c1.016.182 1.45.507 2.278 1.889 1.079 1.799 2.101 3.715 2.93 5.252zm-16.059 2.994c0-.828-.672-1.5-1.5-1.5s-1.5.672-1.5 1.5.672 1.5 1.5 1.5 1.5-.672 1.5-1.5zm10 1c0-.276-.224-.5-.5-.5h-7c-.276 0-.5.224-.5.5s.224.5.5.5h7c.276 0 .5-.224.5-.5zm2.941-5.527s-.74-1.826-1.631-3.142c-.202-.298-.515-.502-.869-.566-1.511-.272-2.835-.359-4.441-.359s-2.93.087-4.441.359c-.354.064-.667.268-.869.566-.891 1.315-1.631 3.142-1.631 3.142 1.64.313 4.309.497 6.941.497s5.301-.184 6.941-.497zm2.059 4.527c0-.828-.672-1.5-1.5-1.5s-1.5.672-1.5 1.5.672 1.5 1.5 1.5 1.5-.672 1.5-1.5zm-18.298-6.5h-2.202c-.276 0-.5.224-.5.5v.511c0 .793.926.989 1.616.989l1.086-2z",
    fillColor: "#9B59B6", // Purple to match driver theme
    fillOpacity: 1,
    strokeWeight: 1,
    strokeColor: "#FFFFFF",
    rotation: 0,
    scale: 1,
  };

  // Haversine formula to calculate distance
  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371; // Earth radius in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  };

  useEffect(() => {
    const fetchTripDetails = async () => {
      try {
        const response = await fetch(`${API_URL}/trip/${tripId}`);
        const data = await response.json();
        setTripData(data);
      } catch (error) {
        console.error("Error fetching trip details:", error);
      }
    };

    fetchTripDetails();
  }, [tripId, API_URL]);

  useEffect(() => {
    if (tripData) {
      const fetchDriverLocation = async () => {
        try {
          const response = await fetch(
            `${API_URL}/driver/${tripData[0].driver_id}`
          );
          const driverArray = await response.json();
          if (Array.isArray(driverArray) && driverArray.length > 0) {
            const driver = driverArray[0];
            const lat = parseFloat(driver.lat_cordinates);
            const lng = parseFloat(driver.long_cordinates);
            if (!isNaN(lat) && !isNaN(lng)) {
              setDriverLocation({ latitude: lat, longitude: lng });
            } else {
              console.error("Invalid coordinate values:", driver);
            }
          } else {
            console.error("No driver data found or unexpected format");
          }
        } catch (error) {
          console.error("Error fetching driver location:", error);
        } finally {
          setLoadingDriver(false);
        }
      };

      fetchDriverLocation(); // Initial fetch
      const intervalId = setInterval(fetchDriverLocation, 15000); // Fetch every 15 seconds to match Director

      return () => clearInterval(intervalId); // Cleanup on unmount
    }
  }, [tripData, API_URL]);

  useEffect(() => {
    if (mapLoaded && tripData && window.google && window.google.maps) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: new window.google.maps.LatLng(
            tripData[0].origin_location_lat,
            tripData[0].origin_location_long
          ),
          destination: new window.google.maps.LatLng(
            tripData[0].destination_lat,
            tripData[0].destination_long
          ),
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirections(result);
          } else {
            console.error("Error fetching directions:", result);
          }
        }
      );
    }
  }, [mapLoaded, tripData]);

  // Check if driver is off route
  const checkIfOffRoute = (driverPos, routePoints) => {
    if (!routePoints || !driverPos) return false;

    const threshold = 0.1; // 100 meters threshold
    return !routePoints.some(
      (point) =>
        haversineDistance(
          driverPos.lat,
          driverPos.lng,
          point.lat(),
          point.lng()
        ) <= threshold
    );
  };

  const mapContainerStyle = {
    height: "100vh", // Full screen height
    width: "100%", // Full screen width
  };

  const center = driverLocation
    ? { lat: driverLocation.latitude, lng: driverLocation.longitude }
    : {
        lat: tripData?.[0]?.origin_location_lat || 0,
        lng: tripData?.[0]?.origin_location_long || 0,
      };

  // Render loading state if trip data or driver location is not loaded
  if (!tripData || loadingDriver) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B59B6] mx-auto mb-4"></div>
          <p className="text-lg text-gray-700 dark:text-gray-300 font-medium">Loading trip tracker...</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Please wait while we locate your driver</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto h-full"
      >
        {isOffRoute && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-red-100 dark:bg-red-900 rounded-lg flex items-center space-x-2"
          >
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            <span className="text-red-600 dark:text-red-400">
              Driver is off the designated route!
            </span>
          </motion.div>
        )}

        {/* Map Preview Overlay with Stats */}
        <div 
          className="absolute top-12 left-16 right-16 bg-purple-900 bg-opacity-65 backdrop-blur-sm rounded-xl border-2 border-purple-600 shadow-lg p-4 z-10"
          style={{
            backgroundColor: 'rgba(88, 24, 117, 0.65)', // More transparent 
            borderColor: 'rgba(155, 89, 182, 0.9)',
            paddingVertical: '12px',
            paddingHorizontal: '16px',
            borderRadius: '12px',
            top: '48px', // Move higher up on screen
          }}
        >
          <div className="flex justify-around items-center">
            <div className="text-center">
              <div className="w-3 h-3 mb-1 mx-auto">
                <svg viewBox="0 0 24 24" fill="white">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                </svg>
              </div>
              <div className="text-white font-bold text-lg">
                {calculateETA() ? `${calculateETA()} min` : "N/A"}
              </div>
              <div className="text-purple-200 text-xs">away</div>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 mb-1 mx-auto">
                <svg viewBox="0 0 24 24" fill="white">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div className="text-white font-bold text-lg">
                {calculateCostPerKm() ? `$${calculateCostPerKm()}` : "N/A"}
              </div>
              <div className="text-purple-200 text-xs">per km</div>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 mb-1 mx-auto">
                <svg viewBox="0 0 24 24" fill="white">
                  <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5c-.66 0-1.21.42-1.42 1.01L13 13h4l3.92-6.99zM9.5 9c.83 0 1.5-.67 1.5-1.5S10.33 6 9.5 6 8 6.67 8 7.5 8.67 9 9.5 9zm-2.5 4h1.42l6.58-6.59c.56-.56.56-1.47 0-2.04-.28-.28-.61-.43-.98-.48V4c0-.55-.45-1-1-1H6c-.55 0-1 .45-1 1v.89c-.37.05-.7.2-.98.48-.56.56-.56 1.47 0 2.04L10.08 13H7v2h5v-2z"/>
                </svg>
              </div>
              <div className="text-white font-bold text-lg">
                {tripData?.[0]?.distance ? `${tripData[0].distance} km` : "N/A"}
              </div>
              <div className="text-purple-200 text-xs">away</div>
            </div>
          </div>
        </div>

        <LoadScript
          googleMapsApiKey={MAPS_CONFIG.GOOGLE_MAPS_API_KEY}
          onLoad={() => setMapLoaded(true)}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={15}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
              styles: [
                {
                  featureType: "all",
                  elementType: "geometry",
                  stylers: [{ saturation: -80 }],
                },
              ],
            }}
          >
            {tripData && mapLoaded && window.google && window.google.maps && (
              <>
                <Marker
                  position={{
                    lat: tripData[0].origin_location_lat,
                    lng: tripData[0].origin_location_long,
                  }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 18,
                    fillColor: "#4CAF50", // Material Design Green
                    fillOpacity: 1,
                    strokeColor: "#9B59B6", // Purple outline
                    strokeWeight: 3,
                    labelOrigin: new window.google.maps.Point(0, 0)
                  }}
                  label={{
                    text: "O",
                    color: "#FFFFFF",
                    fontSize: "12px",
                    fontWeight: "bold"
                  }}
                />
                <Marker
                  position={{
                    lat: tripData[0].destination_lat,
                    lng: tripData[0].destination_long,
                  }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 18,
                    fillColor: "#E74C3C", // Material Design Red
                    fillOpacity: 1,
                    strokeColor: "#9B59B6", // Purple outline
                    strokeWeight: 3,
                    labelOrigin: new window.google.maps.Point(0, 0)
                  }}
                  label={{
                    text: "D",
                    color: "#FFFFFF",
                    fontSize: "12px",
                    fontWeight: "bold"
                  }}
                />
                {driverLocation && mapLoaded && window.google && window.google.maps && (
                  <>
                    {/* Outer glow marker for driver */}
                    <Marker 
                      position={{
                        lat: driverLocation.latitude,
                        lng: driverLocation.longitude,
                      }}
                      title="Driver Current Location" 
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 24,
                        fillColor: "#9B59B6", // Vibrant purple
                        fillOpacity: 0.3,
                        strokeColor: "transparent",
                        strokeWeight: 0,
                      }}
                    />
                    {/* Main marker - purple car with emoji */}
                    <Marker 
                      position={{
                        lat: driverLocation.latitude,
                        lng: driverLocation.longitude,
                      }}
                      title="Driver Current Location" 
                      label="🚗"
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 16,
                        fillColor: "#9B59B6", // Vibrant purple
                        fillOpacity: 1,
                        strokeColor: "#FFFFFF",
                        strokeWeight: 3,
                        labelOrigin: new window.google.maps.Point(0, 0)
                      }}
                    />
                  </>
                )}
                {directions && (
                  <DirectionsRenderer
                    directions={directions}
                    options={{
                      suppressMarkers: true,
                      polylineOptions: {
                        strokeColor: "#9B59B6", // Purple to match driver marker
                        strokeWeight: 5,
                        strokeOpacity: 0.8,
                      },
                    }}
                  />
                )}
              </>
            )}
          </GoogleMap>
        </LoadScript>
      </motion.div>
    </div>
  );
};

export default TripTracker2;